import { getInputValidationError, isBlankText, normalizeInputValue, type InputRuleTranslate, type InputWhitespaceType } from './inputRules';

export type EmployeeImportType = 'brand' | 'shop';
export interface ImportInputError { name: string; failReason: string; row?: number }
interface Column { header: string; field: string; strategy: InputWhitespaceType; maxLength?: number }
// v12-on-prem 的两个下载模板均为手机号模板：第 1 行说明，第 2 行中文表头。
// 只接受后端明确支持的列，不根据位置猜测字段，也不改写说明或外部数据。
const commonColumns: Column[] = [
  { header: '姓名', field: 'nickName', strategy: 'naturalText', maxLength: 30 },
  { header: '国家区号', field: 'callingCode', strategy: 'systemCode' },
  { header: '手机号', field: 'phonenumber', strategy: 'phone' },
];
export const employeeImportColumns = (type: EmployeeImportType): Column[] => [
  ...commonColumns,
  ...(type === 'brand' ? [{ header: '部门名称', field: 'deptName', strategy: 'naturalText' as const, maxLength: 30 }] : [
    { header: '门店编号', field: 'shopCode', strategy: 'systemCode' as const },
    { header: '角色名称', field: 'roleName', strategy: 'naturalText' as const },
  ]),
];

/** 预检和正式导入共同使用返回的文件副本；原文件和表单选中的文件保持原样。 */
export async function prepareEmployeeImport(file: File, type: EmployeeImportType, t: InputRuleTranslate): Promise<{ file: File | null; errors: ImportInputError[] }> {
  const errorResult = (key: string) => ({ file: null, errors: [{ name: file.name, failReason: t(key) }] });
  if (!/\.xlsx?$/i.test(file.name)) return errorResult('excel.typew');
  try {
    const xlsx = await import('xlsx');
    const book = xlsx.read(await file.arrayBuffer(), { type: 'array', cellFormula: true, cellDates: true, cellNF: true, cellStyles: true });
    const sheet = book.Sheets[book.SheetNames[0]];
    if (!sheet?.['!ref']) return errorResult('excel.invalidTemplate');
    const range = xlsx.utils.decode_range(sheet['!ref']);
    const columns = employeeImportColumns(type);
    const mapped: { index: number; column: Column }[] = [];
    for (let index = range.s.c; index <= range.e.c; index++) {
      const cell = sheet[xlsx.utils.encode_cell({ r: 1, c: index })];
      const header = cell?.v == null ? '' : String(cell.v);
      if (cell?.f) return errorResult('excel.invalidTemplate');
      if (!header) {
        // 无表头但有内容的额外列不能绕过字段处理。
        for (let row = 2; row <= range.e.r; row++) {
          const extra = sheet[xlsx.utils.encode_cell({ r: row, c: index })];
          if (extra?.v != null || extra?.f) return errorResult('excel.invalidTemplate');
        }
        continue;
      }
      const column = columns.find(item => item.header === header);
      if (!column || cell.f || mapped.some(item => item.column.field === column.field)) return errorResult('excel.invalidTemplate');
      mapped.push({ index, column });
    }
    if (mapped.length !== columns.length) return errorResult('excel.invalidTemplate');
    const errors: ImportInputError[] = [];
    let rowCount = 0;
    for (let row = 2; row <= range.e.r; row++) {
      if (mapped.every(({ index }) => { const cell = sheet[xlsx.utils.encode_cell({ r: row, c: index })]; return !cell?.f && (cell?.v == null || cell.v === ''); })) continue;
      rowCount++;
      for (const { index, column } of mapped) {
        const address = xlsx.utils.encode_cell({ r: row, c: index });
        const cell = sheet[address];
        const fail = (message: string) => errors.push({ name: t('excel.rowField', { row: row + 1, field: column.header }), failReason: message, row: row + 1 });
        if (cell?.f || (cell && (!['s', 'n', 'z'].includes(cell.t) || (cell.t === 'n' && cell.z && xlsx.SSF.is_date(cell.z))))) { fail(t('excel.textCellRequired')); continue; }
        // 整数型手机号可无损转换；门店编号必须保留真实的六位数字，不能猜补前导零。
        const raw = cell?.v == null ? '' : String(cell.v);
        const value = normalizeInputValue(raw, column.strategy);
        let message = getInputValidationError(t, value, { strategy: column.strategy, required: true, maxLength: column.maxLength });
        if (!message && column.field === 'nickName' && !/^[\p{L}\p{N}\p{M}\p{Zs}._\-·'’]{1,30}$/u.test(value)) message = t('excel.employeeName');
        if (!message && column.field === 'phonenumber' && !/^1[3-9]\d{9}$/.test(value)) message = t('excel.phoneFormat');
        if (!message && column.field === 'shopCode' && !/^\d{6}$/.test(value)) message = t('excel.shopCodeFormat');
        if (!message && column.field === 'callingCode' && /[\p{White_Space}\p{Cf}]/u.test(value)) message = t('excel.internalWhitespace');
        if (!message && isBlankText(value)) message = t('Shared.pleaseEnter');
        if (message) { fail(message); continue; }
        sheet[address] = { ...cell, t: 's', v: value };
        delete sheet[address].w;
        delete sheet[address].h;
        delete sheet[address].r;
      }
    }
    if (!rowCount) return errorResult('excel.emptyData');
    if (errors.length) return { file: null, errors };
    // 统一生成 xlsx 副本，不修改用户原文件；保留第 1 行说明及第 2 行表头。
    const bytes = xlsx.write(book, { type: 'array', bookType: 'xlsx' });
    return { file: new File([bytes], file.name.replace(/\.xlsx?$/i, '.xlsx'), { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), errors: [] };
  } catch {
    return errorResult('excel.invalidTemplate');
  }
}
